import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';

const toIntSeconds = (v) => {
    if (v === null) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return undefined;
    return Math.max(0, Math.floor(n));
};

const Timer = forwardRef(({ totalSeconds, initialMinutes = 15, onTimeUp, isActive, color, label }, ref) => {
    const isServerDriven = totalSeconds !== undefined;

    const initialLocalSeconds = useMemo(() => {
        if (isServerDriven) {
            const s = toIntSeconds(totalSeconds);
            if (s === null) return null;
            if (s === undefined) return 0;
            return s;
        }

        const m = Number(initialMinutes);
        if (!Number.isFinite(m) || m < 0) return 0;
        return Math.max(0, Math.floor(m * 60));
    }, [isServerDriven, totalSeconds, initialMinutes]);

    const [localSecondsLeft, setLocalSecondsLeft] = useState(() => (initialLocalSeconds === null ? 0 : initialLocalSeconds));

    // Timer SYNC
    useEffect(() => {
        if (!isServerDriven) return;

        const s = toIntSeconds(totalSeconds);
        if (s === null) return;

        if (s === undefined) {
            setLocalSecondsLeft(0);
            return;
        }

        if (Math.abs(localSecondsLeft - s) > 5) {
            setLocalSecondsLeft(s);
        }
        
    }, [isServerDriven, totalSeconds, localSecondsLeft]);

    useEffect(() => {
        
        if (!isActive) return;

        if (localSecondsLeft <= 0) {
            if (onTimeUp) onTimeUp();
            return;
        }

        const interval = setInterval(() => {
            setLocalSecondsLeft((t) => Math.max(0, t - 1));
        }, 1000);

        return () => clearInterval(interval);
    }, [isActive, localSecondsLeft, onTimeUp]); 

    const displaySeconds = isServerDriven
        ? (totalSeconds === null ? null : localSecondsLeft)
        : localSecondsLeft;

    useImperativeHandle(ref, () => ({
        getTime: () => {
            if (displaySeconds === null) {
                return { minutes: null, seconds: null, totalSeconds: null };
            }

            const mins = Math.floor(displaySeconds / 60);
            const secs = displaySeconds % 60;

            return {
                minutes: mins,
                seconds: secs,
                totalSeconds: displaySeconds
            };
        }
    }), [displaySeconds]);

    const title = label || color || 'Timer';

    const formatted = useMemo(() => {
        if (displaySeconds === null) return '∞';
        const mins = Math.floor(displaySeconds / 60);
        const secs = displaySeconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }, [displaySeconds]);

    const isLow = displaySeconds !== null && displaySeconds <= 60;

    return (
        <div
            className={`timer-display p-2 rounded ${isActive ? 'bg-success text-white' : 'bg-secondary text-light'}`}
            style={{ minWidth: '120px' }}
        >
            <div className="small text-uppercase mb-1" style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                {title}
            </div>
            <h4 className={`m-0 ${isLow && isActive ? 'text-warning' : 'text-white'}`}>
                {formatted}
            </h4>
        </div>
    );
});

export default Timer;